import React from 'react';
import PropTypes from 'prop-types';
import { CheckCircle, Edit3, FileText, User, MapPin, Briefcase, DollarSign, Wallet, CreditCard } from 'lucide-react';
import { SectionHeader, FormNavigation, FormCheckbox } from '../FormComponents';

const formatCurrency = (value) => {
  const num = parseFloat(value) || 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  }).format(num);
};

const formatPhone = (val) => {
  const digits = (val || '').replace(/\D/g, '');
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const formatSSN = (val) => {
  if (!val || val.length < 4) return '***-**-****';
  return `***-**-${val.slice(-4)}`;
};

function SummarySection({ title, icon, onEdit, children }) {
  return (
    <div
      style={{
        marginBottom: '20px',
        padding: '20px',
        borderRadius: '12px',
        background: 'rgba(255, 255, 255, 0.03)',
        border: '1px solid rgba(148, 163, 184, 0.2)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
          paddingBottom: '12px',
          borderBottom: '1px solid rgba(148, 163, 184, 0.15)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ color: '#38bdf8' }}>{icon}</span>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#e2e8f0' }}>
            {title}
          </h3>
        </div>
        <button
          type="button"
          onClick={onEdit}
          style={{
            background: 'none',
            border: 'none',
            color: '#38bdf8',
            cursor: 'pointer',
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <Edit3 size={14} /> Edit
        </button>
      </div>
      <div style={{ color: '#94a3b8', fontSize: '14px', lineHeight: 1.8 }}>
        {children}
      </div>
    </div>
  );
}

SummarySection.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.node.isRequired,
  onEdit: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

function SummaryRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
      <span>{label}:</span>
      <span style={{ color: '#e2e8f0', fontWeight: 500 }}>{value || '—'}</span>
    </div>
  );
}

SummaryRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
};

export default function ReviewSubmit({ data, onBack, onSubmit, onEditSection }) {
  // All hooks must be at the top
  const [agreed, setAgreed] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState('');

  const gettingStarted = data.gettingStarted || {};
  const personalInfo = data.personalInfo || {};
  const contactInfo = data.contactInfo || {};
  const employment = data.employment || {};
  const income = data.income || {};
  const assets = data.assets || {};
  const liabilities = data.liabilities || {};

  const totalIncome = (income.incomes || []).reduce(
    (sum, inc) => sum + (parseFloat(inc.amount) || 0),
    0
  );

  const totalAssets = (assets.assets || []).reduce(
    (sum, asset) => sum + (parseFloat(asset.balance) || 0),
    0
  );

  const totalLiabilities = (liabilities.liabilities || []).reduce(
    (sum, l) => sum + (parseFloat(l.balance) || 0),
    0
  );

  const totalMonthlyDebt = (liabilities.liabilities || [])
    .filter(l => !l.willPayOff)
    .reduce((sum, l) => sum + (parseFloat(l.monthlyPayment) || 0), 0);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError('');
    try {
      await onSubmit();
      // Success - parent will transition to next step
    } catch (error) {
      setSubmitError(error.message || 'Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const loanPurposeLabels = {
    purchase: 'Purchase',
    refinance: 'Refinance',
    cashout: 'Cash-Out Refinance',
  };

  const propertyTypeLabels = {
    'single-family': 'Single Family',
    condo: 'Condo',
    townhouse: 'Townhouse',
    'multi-family': 'Multi-Family',
  };

  const propertyUseLabels = {
    primary: 'Primary Residence',
    secondary: 'Second Home',
    investment: 'Investment Property',
  };

  return (
    <div>
      <SectionHeader
        title="Review Your Application"
        subtitle="Please review your information before submitting"
        icon={<FileText size={28} />}
      />

      {/* Financial Summary Card */}
      <div
        style={{
          marginBottom: '24px',
          padding: '24px',
          borderRadius: '16px',
          background: 'linear-gradient(135deg, rgba(56, 189, 248, 0.1), rgba(99, 102, 241, 0.05))',
          border: '1px solid rgba(56, 189, 248, 0.2)',
        }}
      >
        <h3 style={{ margin: '0 0 20px', fontSize: '18px', fontWeight: 600, color: '#e2e8f0', textAlign: 'center' }}>
          Financial Summary
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', textAlign: 'center' }}>
          <div>
            <p style={{ margin: '0 0 4px', fontSize: '12px', color: '#94a3b8' }}>Monthly Income</p>
            <p style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#22c55e' }}>
              {formatCurrency(totalIncome)}
            </p>
          </div>
          <div>
            <p style={{ margin: '0 0 4px', fontSize: '12px', color: '#94a3b8' }}>Total Assets</p>
            <p style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#38bdf8' }}>
              {formatCurrency(totalAssets)}
            </p>
          </div>
          <div>
            <p style={{ margin: '0 0 4px', fontSize: '12px', color: '#94a3b8' }}>Monthly Debt</p>
            <p style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#f87171' }}>
              {formatCurrency(totalMonthlyDebt)}
            </p>
          </div>
        </div>
      </div>

      <SummarySection title="Loan Information" icon={<FileText size={18} />} onEdit={() => onEditSection(0)}>
        <SummaryRow label="Loan Purpose" value={loanPurposeLabels[gettingStarted.loanPurpose]} />
        <SummaryRow label="Property Type" value={propertyTypeLabels[gettingStarted.propertyType]} />
        <SummaryRow label="Property Use" value={propertyUseLabels[gettingStarted.propertyUse]} />
      </SummarySection>

      <SummarySection title="Personal Information" icon={<User size={18} />} onEdit={() => onEditSection(1)}>
        <SummaryRow
          label="Name"
          value={`${personalInfo.firstName || ''} ${personalInfo.middleName || ''} ${personalInfo.lastName || ''} ${personalInfo.suffix || ''}`.trim()}
        />
        <SummaryRow label="Date of Birth" value={personalInfo.dateOfBirth} />
        <SummaryRow label="SSN" value={formatSSN(personalInfo.ssn)} />
        <SummaryRow label="Citizenship" value={personalInfo.citizenship?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} />
        <SummaryRow label="Marital Status" value={personalInfo.maritalStatus?.replace(/\b\w/g, l => l.toUpperCase())} />
      </SummarySection>

      <SummarySection title="Contact Information" icon={<MapPin size={18} />} onEdit={() => onEditSection(2)}>
        <SummaryRow
          label="Address"
          value={`${contactInfo.streetAddress || ''} ${contactInfo.unit || ''}, ${contactInfo.city || ''}, ${contactInfo.state || ''} ${contactInfo.zipCode || ''}`}
        />
        <SummaryRow label="Phone" value={formatPhone(contactInfo.phone)} />
        <SummaryRow label="Email" value={contactInfo.email} />
      </SummarySection>

      <SummarySection title="Employment" icon={<Briefcase size={18} />} onEdit={() => onEditSection(3)}>
        <SummaryRow label="Status" value={employment.employmentStatus?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} />
        {employment.employerName && <SummaryRow label="Employer" value={employment.employerName} />}
        {employment.jobTitle && <SummaryRow label="Position" value={employment.jobTitle} />}
        {employment.monthlyIncome && <SummaryRow label="Monthly Income" value={formatCurrency(employment.monthlyIncome)} />}
      </SummarySection>

      <SummarySection title="Income Sources" icon={<DollarSign size={18} />} onEdit={() => onEditSection(4)}>
        {(income.incomes || []).map((inc, i) => (
          <SummaryRow
            key={i}
            label={inc.type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            value={formatCurrency(inc.amount)}
          />
        ))}
        <div style={{ borderTop: '1px solid rgba(148, 163, 184, 0.2)', marginTop: '8px', paddingTop: '8px' }}>
          <SummaryRow label="Total Monthly Income" value={formatCurrency(totalIncome)} />
        </div>
      </SummarySection>

      <SummarySection title="Assets" icon={<Wallet size={18} />} onEdit={() => onEditSection(5)}>
        {(assets.assets || []).length === 0 ? (
          <p style={{ margin: 0, fontStyle: 'italic' }}>No assets listed</p>
        ) : (
          <>
            {(assets.assets || []).map((asset, i) => (
              <SummaryRow
                key={i}
                label={`${asset.type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} - ${asset.institution || 'N/A'}`}
                value={formatCurrency(asset.balance)}
              />
            ))}
            <div style={{ borderTop: '1px solid rgba(148, 163, 184, 0.2)', marginTop: '8px', paddingTop: '8px' }}>
              <SummaryRow label="Total Assets" value={formatCurrency(totalAssets)} />
            </div>
          </>
        )}
      </SummarySection>

      <SummarySection title="Liabilities" icon={<CreditCard size={18} />} onEdit={() => onEditSection(6)}>
        {(liabilities.liabilities || []).length === 0 ? (
          <p style={{ margin: 0, fontStyle: 'italic' }}>No liabilities listed</p>
        ) : (
          <>
            {(liabilities.liabilities || []).map((l, i) => (
              <SummaryRow
                key={i}
                label={`${l.type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} - ${l.creditor || 'N/A'}${l.willPayOff ? ' (Pay off)' : ''}`}
                value={`${formatCurrency(l.balance)} (${formatCurrency(l.monthlyPayment)}/mo)`}
              />
            ))}
            <div style={{ borderTop: '1px solid rgba(148, 163, 184, 0.2)', marginTop: '8px', paddingTop: '8px' }}>
              <SummaryRow label="Total Liabilities" value={formatCurrency(totalLiabilities)} />
              <SummaryRow label="Monthly Payments" value={formatCurrency(totalMonthlyDebt)} />
            </div>
          </>
        )}
      </SummarySection>

      {/* Agreement checkbox */}
      <div
        style={{
          padding: '20px',
          borderRadius: '12px',
          background: 'rgba(34, 197, 94, 0.05)',
          border: '1px solid rgba(34, 197, 94, 0.2)',
          marginBottom: '24px',
        }}
      >
        <FormCheckbox
          label="I certify that all information provided is true and complete"
          checked={agreed}
          onChange={setAgreed}
          description="By checking this box, you agree that the information provided is accurate to the best of your knowledge."
        />
      </div>

      {submitError && (
        <div
          style={{
            padding: '12px 16px',
            borderRadius: '8px',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            marginBottom: '16px',
            color: '#fca5a5',
            fontSize: '14px',
          }}
        >
          {submitError}
        </div>
      )}

      <FormNavigation
        onBack={onBack}
        onNext={handleSubmit}
        nextLabel={isSubmitting ? 'Submitting...' : 'Submit Application'}
        isLastSection={true}
        isValid={agreed && !isSubmitting}
      />
    </div>
  );
}

ReviewSubmit.propTypes = {
  data: PropTypes.shape({
    gettingStarted: PropTypes.object,
    personalInfo: PropTypes.object,
    contactInfo: PropTypes.object,
    employment: PropTypes.object,
    income: PropTypes.object,
    assets: PropTypes.object,
    liabilities: PropTypes.object,
  }).isRequired,
  onBack: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onEditSection: PropTypes.func.isRequired,
};
